/**
 * Randomness, split into two clearly separated jobs.
 *
 * 1. THE DECISION — LONG or SHORT. Drawn from `crypto.getRandomValues` and
 *    nothing else. `Math.random` is never involved, directly or indirectly.
 *
 * 2. THE PERFORMANCE — how Jeff wanders on his way there. This is cosmetic, so
 *    it uses a small seeded PRNG. The seed itself comes from Web Crypto, which
 *    makes every walk unique in production and exactly reproducible in tests.
 *
 * The decision is drawn *before* the animation starts. The animation is then
 * built to arrive at the bowl that was already chosen; it can never change it.
 */

import type { Direction } from '../../shared/types.ts';

const U32_SPACE = 0x1_0000_0000; // 2^32

export interface Decision {
  direction: Direction;
  /**
   * The exact 32-bit draw, so the decision is auditable rather than merely
   * asserted. Published with the call as verification metadata.
   */
  draw: number;
  /** Draws strictly below this go LONG. */
  threshold: number;
  /** Hex of the four bytes that produced `draw`. */
  entropy: string;
}

export function secureRandomBytes(length: number): Uint8Array {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return bytes;
}

export function toHex(bytes: Uint8Array): string {
  let out = '';
  for (const b of bytes) out += b.toString(16).padStart(2, '0');
  return out;
}

/**
 * Draws a uniform 32-bit unsigned integer from the CSPRNG.
 *
 * Returned alongside its bytes so callers can publish the exact entropy that
 * produced a decision.
 */
export function secureU32(): { value: number; bytes: Uint8Array } {
  const bytes = secureRandomBytes(4);
  // Big-endian assembly; `>>> 0` keeps it in the unsigned 32-bit range.
  const value = (((bytes[0]! << 24) | (bytes[1]! << 16) | (bytes[2]! << 8) | bytes[3]!) >>> 0);
  return { value, bytes };
}

/**
 * Makes the LONG / SHORT call.
 *
 * A uniform 32-bit draw is compared against `floor(p * 2^32)`. At the default
 * p = 0.5 the threshold is exactly 2^31, so LONG and SHORT each get exactly
 * 2^31 of the 2^32 possible draws — a true coin, not an approximation. For
 * other values of p the deviation is bounded by 2^-32, which is far below any
 * observable effect. p = 0 and p = 1 are exact because the largest possible
 * draw is 2^32 - 1.
 *
 * @param longProbability Probability of LONG, clamped to [0, 1].
 */
export function decide(longProbability: number): Decision {
  const p = clamp01(longProbability);
  const threshold = Math.floor(p * U32_SPACE);
  const { value, bytes } = secureU32();

  return {
    direction: value < threshold ? 'LONG' : 'SHORT',
    draw: value,
    threshold,
    entropy: toHex(bytes),
  };
}

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0.5;
  return Math.min(1, Math.max(0, n));
}

// ── Cosmetic randomness ─────────────────────────────────────────────────────

/** A deterministic float source in [0, 1). Cosmetic use only. */
export type Rng = () => number;

/**
 * mulberry32. Cheap, good enough for wandering, and — crucially — seedable, so
 * a failing animation test can be replayed exactly.
 *
 * Never use this for the decision.
 */
export function createSeededRng(seed: number): Rng {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** A CSPRNG-derived seed, so each real walk differs. */
export function secureSeed(): number {
  return secureU32().value;
}

export function rngRange(rng: Rng, min: number, max: number): number {
  return min + rng() * (max - min);
}

export function rngInt(rng: Rng, min: number, maxInclusive: number): number {
  return Math.floor(rngRange(rng, min, maxInclusive + 1));
}

export function rngPick<T>(rng: Rng, items: readonly T[]): T {
  return items[Math.floor(rng() * items.length)]!;
}
